export type NameDetectionStatus = 'detected' | 'needs_review' | 'unknown';
export type ReviewStatus = 'needs_review' | 'confirmed' | 'rejected';
export type MatchConfidence = 'high' | 'medium' | 'low';
export type MatchStatus = 'possible' | 'selected' | 'rejected' | 'needs_review';

export interface NameCandidate {
	value: string;
	source: string;
	confidence: MatchConfidence;
}

export interface PlanMetadata {
	participantName: string;
	participantNameStatus: NameDetectionStatus;
	participantNameCandidates: NameCandidate[];
	ndisNumber: string;
	planStartDate: string;
	planEndDate: string;
	managementType: string;
	sourceSnippets: string[];
	warnings: string[];
}

export interface PlanBudgetSection {
	id: string;
	categoryName: string;
	normalizedCategory: string;
	amount: string;
	managementType: string;
	sourceSectionTitle: string;
	sourceSnippet: string;
	status: ReviewStatus;
	warnings: string[];
	notes: string;
}

export interface DetectedChargeCode {
	code: string;
	description: string;
	category: string;
	rate: string;
	sourceSnippet: string;
	status: ReviewStatus;
	notes: string;
}

export interface UnknownLine {
	text: string;
	reason: string;
	sourceSnippet: string;
	status: ReviewStatus;
	notes: string;
}

export interface NdisPlanModel {
	metadata: PlanMetadata;
	budgets: PlanBudgetSection[];
	chargeCodes: DetectedChargeCode[];
	unknownLines: UnknownLine[];
	rawText: string;
	extractionTimestamp: string;
}

const CATEGORY_NORMALIZATION_MAP: ReadonlyArray<{ match: RegExp; key: string }> = [
	{ match: /\bassistance with daily life\b/i, key: 'core_daily_life' },
	{ match: /\bcore supports?\b/i, key: 'core_supports' },
	{ match: /\bcapacity building(?: supports?)?\b/i, key: 'capacity_building' },
	{ match: /\bcapital supports?\b/i, key: 'capital_supports' },
	{ match: /\btransport\b/i, key: 'transport' },
	{ match: /\bconsumables?\b/i, key: 'consumables' },
	{ match: /\bsocial community and civic(?: participation)?\b/i, key: 'social_community' },
	{ match: /\bincreased social and community participation\b/i, key: 'social_community' },
	{ match: /\bsocial,?\s+community and civic participation\b/i, key: 'social_community' },
	{ match: /\bimproved daily living\b/i, key: 'improved_daily_living' },
	{ match: /\bimproved relationships\b/i, key: 'improved_relationships' },
	{ match: /\bcoordination of supports\b/i, key: 'support_coordination' },
	{ match: /\bsupport coordination\b/i, key: 'support_coordination' },
	{ match: /\bassistive technology\b/i, key: 'assistive_technology' },
	{ match: /\bhome modifications?\b/i, key: 'home_modifications' },
	{ match: /\bimproved living arrangements\b/i, key: 'improved_living_arrangements' },
	{ match: /\bimproved life choices\b/i, key: 'improved_life_choices' },
	{ match: /\bimproved health and wellbeing\b/i, key: 'improved_health_and_wellbeing' },
	{ match: /\bfinding and keeping a job\b/i, key: 'finding_and_keeping_a_job' },
];

const TRUSTED_NAME_LABELS = [
	"Participant's name",
	'Participant name',
	'NDIS participant',
	'Participant',
	'Name',
] as const;

const DATE_PATTERN = /\b(?:\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{1,2}\s+(?:Jan|January|Feb|February|Mar|March|Apr|April|May|Jun|June|Jul|July|Aug|August|Sep|Sept|September|Oct|October|Nov|November|Dec|December)\s+\d{2,4})\b/;
const NUMBER_PATTERN = /\b\d{6,12}\b/;

function normaliseSnippet(line: string): string {
	return line.replace(/\s+/g, ' ').trim();
}

function descriptionFromLine(line: string, code: string): string {
	const withoutCode = line.replace(code, '').replace(/\$\s?\d+(?:,\d{3})*(?:\.\d{2})?/g, '').trim();
	return withoutCode.length > 0 ? withoutCode.slice(0, 120) : 'NDIS support item';
}

function categoryFromCode(code: string): string {
	if (code.startsWith('07_') || code.startsWith('15_')) return 'Capacity building';
	if (code.startsWith('01_') || code.startsWith('03_') || code.startsWith('04_')) return 'Core supports';
	return 'Needs review';
}

function extractRate(line: string): string {
	const match = line.match(/\$\s?\d+(?:,\d{3})*(?:\.\d{2})?(?:\s?\/\s?[A-Za-z]+)?/);
	return match?.[0]?.replace(/\s+/g, ' ') ?? '';
}

const BUDGET_CATEGORIES = [
	'core supports', 'core', 'capacity building', 'capacity building supports',
	'capital supports', 'capital', 'stated supports', 'transport',
	'consumables', 'assistive technology', 'home modifications',
	'coordination of supports', 'support coordination',
	'improved living arrangements', 'improved daily living',
	'improved life choices', 'improved relationships',
	'improved health and wellbeing', 'finding and keeping a job',
	'increased social and community participation',
	'assistance with daily life', 'social community and civic participation',
	'social community and civic',
];

const BUDGET_KEYWORDS = /\b(budget|funding|allocation|allocated|total|plan budget|stated|remaining|available)\b/i;

function isBudgetLine(line: string): boolean {
	const lower = line.toLowerCase();
	for (const cat of BUDGET_CATEGORIES) {
		if (lower.includes(cat)) return true;
	}
	return BUDGET_KEYWORDS.test(line);
}

function extractBudgetCategory(line: string): string {
	const lower = line.toLowerCase();
	const sorted = [...BUDGET_CATEGORIES].sort((a, b) => b.length - a.length);
	for (const cat of sorted) {
		if (lower.includes(cat)) {
			return cat.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
		}
	}
	return 'Budget item';
}

function extractBudgetAmount(line: string): string {
	const match = line.match(/\$\s?\d+(?:,\d{3})*(?:\.\d{2})?/);
	return match?.[0]?.replace(/\s+/g, '') ?? '';
}

function extractManagementNote(line: string): string {
	const match = line.match(/\b(self[- ]?managed|plan[- ]?managed|NDIA[- ]?managed|agency[- ]?managed)\b/i);
	return match?.[1] ? normaliseSnippet(match[1]) : '';
}

function nearbyValue(text: string, labels: readonly string[], valuePattern: RegExp): string {
	for (const label of labels) {
		const pattern = new RegExp(`${label}.{0,80}(${valuePattern.source})`, 'i');
		const match = text.match(pattern);
		if (match?.[1]) return normaliseSnippet(match[1]);
	}
	return '';
}

function uniqueCandidates(candidates: NameCandidate[]): NameCandidate[] {
	const seen = new Set<string>();
	const unique: NameCandidate[] = [];
	for (const candidate of candidates) {
		const key = candidate.value.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		unique.push(candidate);
	}
	return unique;
}

function nameLabelSource(label: string): string {
	return `near '${label}' label`;
}

function hasClearNameLabel(source: string): boolean {
	return source.includes("Participant's name") || source.includes('Participant name') || source.includes('NDIS participant');
}

function planSectionTitle(line: string): boolean {
	const normalized = normaliseSnippet(line);
	if (normalized.length === 0 || normalized.length > 90) return false;
	if (/\$\s?\d/.test(normalized) || /\b\d{2}(?:_\d{1,4}){3,5}\b/.test(normalized)) return false;
	return /\b(budget|funding|support|supports|capacity|capital|core|transport|consumables|plan)\b/i.test(normalized);
}

function sourceSnippetForLine(lines: readonly string[], index: number): string {
	return lines.slice(Math.max(0, index - 1), Math.min(lines.length, index + 2)).map(normaliseSnippet).filter(Boolean).join(' | ');
}

export function normalizeCategoryName(category: string): string {
	const cleaned = normaliseSnippet(category);
	for (const entry of CATEGORY_NORMALIZATION_MAP) {
		if (entry.match.test(cleaned)) return entry.key;
	}
	return cleaned
		.toLowerCase()
		.replace(/&/g, ' and ')
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
}

export function extractParticipantName(text: string): { name: string; status: NameDetectionStatus; candidates: NameCandidate[] } {
	const contextLimit = text.slice(0, 500);
	const candidates: NameCandidate[] = [];

	for (const label of TRUSTED_NAME_LABELS) {
		const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\ /g, '\\s+');
		const pattern = new RegExp(
			`\\b${escapedLabel}\\b\\s*(?::|-)?\\s*([A-Z][A-Za-z' -]+\\s+[A-Z][A-Za-z' -]+(?:\\s+[A-Z][A-Za-z' -]+){0,4})(?=\\s*(?:\\n|,|NDIS\\b|Participant\\s+number\\b|Plan\\s+start\\b|Start\\s+date\\b|Date\\s+of\\s+birth\\b|DOB\\b|Address\\b|$))`,
			'g',
		);

		const labelContexts = [contextLimit];
		const labelPattern = new RegExp(`\\b${escapedLabel}\\b`, 'gi');
		for (const match of text.matchAll(labelPattern)) {
			const index = match.index ?? 0;
			if (index < 500) continue;
			labelContexts.push(text.slice(index, index + 250));
		}

		for (const labelContext of labelContexts) {
			for (const match of labelContext.matchAll(pattern)) {
				const value = normaliseSnippet(match[1] ?? '').slice(0, 60);
				if (!value || value.split(/\s+/).length < 2) continue;
				candidates.push({
					value,
					source: nameLabelSource(label),
					confidence: hasClearNameLabel(label) ? 'high' : 'medium',
				});
			}
		}
	}

	const unique = uniqueCandidates(candidates);
	if (unique.length === 0) return { name: '', status: 'unknown', candidates: [] };
	if (unique.length > 1) return { name: unique[0]?.value ?? '', status: 'needs_review', candidates: unique };
	return { name: unique[0]?.value ?? '', status: 'detected', candidates: unique };
}

export function extractBudgets(text: string): PlanBudgetSection[] {
	const lines = text.split(/\r?\n/);
	const budgets: PlanBudgetSection[] = [];
	let currentSectionTitle = '';

	lines.forEach((rawLine, index) => {
		const line = normaliseSnippet(rawLine);
		if (!line) return;

		if (planSectionTitle(line) && !extractBudgetAmount(line)) {
			currentSectionTitle = line;
		}

		if (!isBudgetLine(line) || !extractBudgetAmount(line)) return;

		const categoryName = extractBudgetCategory(line);
		const managementType = extractManagementNote(line);
		const warnings = categoryName === 'Budget item' ? ['Budget category needs review.'] : [];

		budgets.push({
			id: `budget-${budgets.length}`,
			categoryName,
			normalizedCategory: normalizeCategoryName(categoryName),
			amount: extractBudgetAmount(line),
			managementType,
			sourceSectionTitle: currentSectionTitle,
			sourceSnippet: sourceSnippetForLine(lines, index),
			status: 'needs_review',
			warnings,
			notes: '',
		});
	});

	return budgets;
}

export function extractPlanModel(text: string): NdisPlanModel {
	const lines = text
		.split(/\r?\n/)
		.map(normaliseSnippet)
		.filter((line) => line.length > 0);
	const codePattern = /\b\d{2}(?:_\d{1,4}){3,5}\b/;
	const dollarPattern = /\$\s?\d+(?:,\d{3})*(?:\.\d{2})?/;
	const participantName = extractParticipantName(text);
	const budgets = extractBudgets(text);
	const chargeCodes: DetectedChargeCode[] = [];
	const unknownLines: UnknownLine[] = [];
	const managementMatch = text.match(/\b(self-managed|self managed|plan-managed|plan managed|NDIA-managed|NDIA managed|agency-managed|agency managed)\b/i);
	const managementType = managementMatch?.[1] ? normaliseSnippet(managementMatch[1]).replace(/\bndia\b/i, 'NDIA') : '';
	const sourceSnippets = lines.slice(0, 5);
	const metadataWarnings: string[] = [];

	if (participantName.status === 'unknown') metadataWarnings.push('Participant name was not detected near a trusted label.');
	if (participantName.status === 'needs_review') metadataWarnings.push('Multiple participant name candidates were found.');

	for (const line of lines) {
		const hasCode = codePattern.test(line);
		const hasDollar = dollarPattern.test(line);

		if (!hasCode && !hasDollar) continue;

		if (hasCode) {
			const code = line.match(codePattern)?.[0] ?? '';
			chargeCodes.push({
				code,
				description: descriptionFromLine(line, code),
				category: categoryFromCode(code),
				rate: extractRate(line),
				sourceSnippet: line,
				status: 'needs_review',
				notes: '',
			});
		} else if (!isBudgetLine(line)) {
			unknownLines.push({
				text: line,
				reason: 'Dollar amount found but no NDIS item code or budget category detected',
				sourceSnippet: line,
				status: 'needs_review',
				notes: '',
			});
		}
	}

	return {
		metadata: {
			participantName: participantName.name,
			participantNameStatus: participantName.status,
			participantNameCandidates: participantName.candidates,
			ndisNumber: nearbyValue(text, ['NDIS\\s*Number', 'Participant\\s*number'], NUMBER_PATTERN),
			planStartDate: nearbyValue(text, ['plan\\s*start', 'start\\s*date'], DATE_PATTERN),
			planEndDate: nearbyValue(text, ['plan\\s*end', 'end\\s*date'], DATE_PATTERN),
			managementType,
			sourceSnippets,
			warnings: metadataWarnings,
		},
		budgets,
		chargeCodes,
		unknownLines,
		rawText: text,
		extractionTimestamp: new Date().toISOString(),
	};
}
